/**
 * =========================================================================================
 * Billing Payments Page
 * =========================================================================================
 */

import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import * as Icon from 'ubs-icons';
import { COLOR } from 'ubs-framework/v2/common/constant';
import UCustomScrollBar from 'ubs-framework/v2/UCustomScrollBar';
import useUAlert from 'ubs-framework/v2/UAlert';
import PaymentsFilter from './components/payments-filter';
import SubscriptionHeader from './components/subscription-header';
import PaymentsList from './components/payments-list';
import SubscriptionTimelineModal from './components/subscription-timeline-modal';
import { showToastError, showToastSuccess } from 'ubs-framework/v2/common/function';
import useCashfreePayment from '../common/useCashfreePayment';
import { sendData } from '../../../../store/actions';
import useSocket from '../../../common/useSocket';
import AppHeader from '../../../common/components/setting-app-header';


const BillingPayments = () => {
    const alert = useUAlert();
    const history = useHistory();
    const dispatch = useDispatch();
    const _FILTER = useSelector(e => e?.filter?.billingPayments);
    const _ORG_ID = useSelector(e => e.common?.orgId);
    const _PI = useSelector(e => e?.common?.planInfo);
    const _RESUME_PAYMENT = useSelector(e => e.common?.pendingPayment);

    const [currentSubscription, setCurrentSubscription] = useState(null);
    const [summary, setSummary] = useState(null);
    const [paymentsList, setPaymentsList] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [dataLoading, setDataLoading] = useState(false);
    const [resumeLoadingId, setResumeLoadingId] = useState(null);
    const [actionLoading, setActionLoading] = useState(null);
    const [timelineModal, setTimelineModal] = useState({ open: false, subscriptionId: null, subscriptionName: null });

    const { triggerPayment } = useCashfreePayment();
    const payNowTriggeredRef = useRef(false);

    const getData = () => {
        setDataLoading(true);
        const payload = {
            page: _FILTER?.currentPage,
            limit: _FILTER?.limit,
            includeCashfreePayments: true,
            ...(_FILTER?.status && { status: _FILTER.status }),
            ...(_FILTER?.paymentType && { paymentType: _FILTER.paymentType }),
            ...(_FILTER?.fromDate && { fromDate: _FILTER.fromDate }),
            ...(_FILTER?.toDate && { toDate: _FILTER.toDate }),
        };
        sendData(dispatch, 'billing|cashfree|subscription|paymentsList', payload);
    };

    const getResumePayment = () => {
        sendData(dispatch, 'billing|cashfree|subscription|resumePayment', {});
    }

    const { off, refresh } = useSocket(res => {
        if (!res.error) {
            switch (res.event) {
                case 'billing|cashfree|subscription|paymentsList':
                    setDataLoading(false);
                    setCurrentSubscription(res?.data?.currentSubscription || null);
                    setSummary(res?.data?.summary || null);
                    setPaymentsList(res?.data?.rows || []);
                    setTotalRecords(res?.data?.pagination?.total ?? 0);
                    break;
                case 'billing|cashfree|subscription|resumePayment':
                    setResumeLoadingId(null);
                    if (payNowTriggeredRef.current) {
                        payNowTriggeredRef.current = false;
                        const rpAction = res?.data?.action;
                        if (rpAction === 'RESUME_AUTH' || rpAction === 'RESUME_PAYMENT') {
                            triggerPayment(rpAction, res?.data);
                        }
                    }
                    if (res?.data?.action === 'ALREADY_PAID_APPLIED') getData();
                    break;
                case 'billing|cashfree|subscription|pause':
                    setActionLoading(null);
                    showToastSuccess(res?.data?.message || 'Subscription has been paused.');
                    getData();
                    break;
                case 'billing|cashfree|subscription|resume':
                    setActionLoading(null);
                    showToastSuccess(res?.data?.message || 'Subscription has been resumed.');
                    getData();
                    break;
                case 'billing|cashfree|subscription|cancel':
                    setActionLoading(null);
                    showToastSuccess(res?.data?.message || 'Subscription has been cancelled.');
                    getData();
                    break;
                case 'billing|cashfree|subscription|cancelModification':
                    showToastSuccess(res?.data?.message || 'Subscription has been cancelled.');
                    getResumePayment();
                    getData();
                    break;
                default:
                    return true;
            }
        } else {
            setActionLoading(null);
            showToastError(res?.data?.error || 'Something went wrong. Please try again.');
            setDataLoading(false);
        }
    });

    useEffect(() => {
        refresh();
        getData();
        return () => off();
    }, [_FILTER]);

    const handlePause = () => {
        alert.show({
            title: 'Pause Subscription',
            description: 'Are you sure you want to pause your subscription? You will retain access until the end of your current billing period.',
            close: 'Cancel',
            type: 'warning',
            icon: <Icon.Clock fill={COLOR.u_orange} />,
            submit: 'Yes, Pause',
            onSubmit: () => {
                setActionLoading('pause');
                sendData(dispatch, 'billing|cashfree|subscription|pause', { subscriptionId: currentSubscription?.subscriptionId });
            }
        });
    };

    const handleResume = () => {
        alert.show({
            title: 'Reactivate Subscription',
            description: 'Are you sure you want to <strong>reactivate</strong> your subscription?',
            close: 'Cancel',
            type: 'success',
            icon: <Icon.CheckMark fill={COLOR.u_green} />,
            submit: 'Yes, Reactivate',
            onSubmit: () => {
                setActionLoading('resume');
                sendData(dispatch, 'billing|cashfree|subscription|resume', { subscriptionId: currentSubscription?.subscriptionId });
            }
        });
    };

    const handleCancel = () => {
        alert.show({
            title: 'Cancel Subscription',
            description: 'Are you sure you want to <strong>permanently cancel</strong> your subscription? This action cannot be undone.',
            close: 'Go Back',
            icon: <Icon.Delete fill={COLOR.u_red} />,
            submit: 'Yes, Cancel Subscription',
            onSubmit: () => {
                setActionLoading('cancel');
                sendData(dispatch, 'billing|cashfree|subscription|cancel', { subscriptionId: currentSubscription?.subscriptionId });
            }
        });
    };

    const handleCreateNew = () => {
        history.push(`/${_ORG_ID}/manage/pricing-plan`);
    };

    const handleViewTimeline = (sub) => {
        setTimelineModal({
            open: true,
            subscriptionId: sub?.subscriptionId,
            subscriptionName: sub?.planCode || sub?.planKey || sub?.subscriptionId,
        });
    };

    const handle = {
        resumePayment: (row) => {
            setResumeLoadingId(row?.paymentId);
            payNowTriggeredRef.current = true;
            getResumePayment();
        },
        cancelSubscription: (row) => {
            sendData(dispatch, 'billing|cashfree|subscription|cancelModification', { subscriptionId: row?.subscriptionId, orderId: row?.orderId, force: true });
        }
    }

    return (
        <React.Fragment>
            <div className="u_layout-list">
                <AppHeader title={`${totalRecords} Payments`}>
                    <PaymentsFilter />
                </AppHeader>
                <div className="u_layout-scrollview u_flex u_direction-column u_overflow-hidden">
                    {_RESUME_PAYMENT?.action === 'SHOW_UPCOMING_PAYMENT_REMINDER' && <div className="u_flex-align u_py-10 u_px-20 u_bg-lightblue u_bb-light">
                        <Icon.InfoTooltip size={18} fill={COLOR?.u_blue} className="u_mr-8" />
                        <p className="u_small-font u_color-title u_fw-500">
                            {_RESUME_PAYMENT?.data?.message || 'Your next payment is scheduled in few days. Please ensure your payment method is ready.'}
                        </p>
                    </div>}
                    <div className="u_px-16 u_pt-16 u_subsciption-header u_flex-shrink">
                        <SubscriptionHeader
                            currentSubscription={currentSubscription}
                            summary={summary}
                            onPause={handlePause}
                            onResume={handleResume}
                            onCancel={handleCancel}
                            onCreateNew={handleCreateNew}
                            onViewTimeline={handleViewTimeline}
                            actionLoading={actionLoading}
                        />
                    </div>
                    <div className="u_flex-1 u_overflow-hidden">
                        <UCustomScrollBar>
                            <PaymentsList
                                paymentsList={paymentsList}
                                totalRecords={totalRecords}
                                dataLoading={dataLoading}
                                resumeLoadingId={resumeLoadingId}
                                onResumePayment={handle.resumePayment}
                                cancelSubscription={handle.cancelSubscription}
                            />
                        </UCustomScrollBar>
                    </div>
                </div>
            </div>

            {timelineModal.open && <SubscriptionTimelineModal
                open={timelineModal.open}
                onClose={() => setTimelineModal({ open: false, subscriptionId: null, subscriptionName: null })}
                subscriptionId={timelineModal.subscriptionId}
                subscriptionName={timelineModal.subscriptionName}
            />}
        </React.Fragment>
    );
};

export default BillingPayments;
